import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography} from '@mui/material';
import Button from '../../../components/base/button/button';
import Form, {MuiRFF} from '../../../components/form';
import Grid2 from '@mui/material/Unstable_Grid2';
import * as Yup from 'yup';
import ValidateMessages from '../../../../core/models/static/texts/vallidate-messages';
import useIsMounted from '../../../hooks/use-is-mounted';
// @ts-ignore
import {ReactComponent as CheckboxIcon} from "../../../../assets/images/check-box/checkbox.svg";
// @ts-ignore
import {ReactComponent as ActiveCheckboxIcon} from "../../../../assets/images/check-box/checkbox-active.svg";
import {stringifyUrl} from "query-string";
import Dialog from "../../base/dialog";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import useSavedComponentsState from "../../../hooks/use-saved-components-state";

interface PrintOrderConfigurationsDialogProps {
    open: boolean;
    onClose: () => void;
    printUrl: string,
}

const schema = Yup.object().shape({
    showAddresses: Yup.boolean().required(ValidateMessages.required).default(false),
    showPaymentTerms: Yup.boolean().required(ValidateMessages.required).default(false),
    showPrices: Yup.boolean().required(ValidateMessages.required).default(false),
    showTransactions: Yup.boolean().required(ValidateMessages.required).default(false),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const PrintOrderConfigurationsDialog = ({open, onClose, printUrl}: PrintOrderConfigurationsDialogProps) => {
    const [defaultSettings, setDefaultSettings] = useState<any>({});
    const [loadingState, setLoadingState] = useState(true);
    const [hasDefaultSettings, setHasDefaultSettings] = useState(false);
    const isMounted = useIsMounted();
    const {highestVersion: getCachedSettings, save: saveCachedSettings} = useSavedComponentsState({name: "PrintOrderConfigs", version: '1.0.0'});

    const initialValues = useMemo(() => ({
        showAddresses: defaultSettings?.showAddresses ?? true,
        showPrices: defaultSettings.showPrices ?? true,
        showPaymentTerms: defaultSettings?.showPaymentTerms ?? true,
        showTransactions: (defaultSettings.showPrices ?? true) ? (defaultSettings?.showTransactions ?? true) : false,
    }), [defaultSettings?.showAddresses, defaultSettings?.showPaymentTerms, defaultSettings.showPrices, defaultSettings?.showTransactions])

    /**
     * Fetches the default settings for this dialog
     * @type {(function(): Promise<void>)}
     */
    const getDefaultSettings = useCallback(async () => {
        setLoadingState(true);
        const cachedSettings = await getCachedSettings();
        if (!isMounted())
            return
        setHasDefaultSettings(!!cachedSettings?.data);
        if (cachedSettings?.data) {
            setDefaultSettings(cachedSettings?.data);
        }
        setLoadingState(false);
    }, [getCachedSettings, isMounted])

    /**
     * As soon as the component mounts, fetch the default settings
     */
    useEffect(() => {
        if (!open)
            return;
        void getDefaultSettings();
    }, [getDefaultSettings, open])

    /**
     * Opens the link with the current
     */
    const handleSubmit = useCallback((values: any) => {
        if (hasDefaultSettings) {
            void saveCachedSettings(values);
        }
        window.open(window.location.origin + stringifyUrl({
            url: printUrl,
            query: {
                showAddresses: values.showAddresses,
                showPaymentTerms: values.showPaymentTerms,
                showPrices: values.showPrices,
                showTransactions: values.showTransactions,
            }
        }), '_blank');
    }, [hasDefaultSettings, printUrl, saveCachedSettings]);

    return (
        <Dialog
            open={open}
            setOpen={() => onClose()}
            maxWidth={'sm'}
        >
            <DialogTitle>
                Printing Options
            </DialogTitle>
            {
                loadingState &&
                <Box sx={{marginBlock: 10}}>
                    <LoadingLottieAnimation width={'80%'}/>
                </Box>
            }
            {
                !loadingState &&
                <Form
                    onSubmit={handleSubmit}
                    validate={validate}
                    formProps={{id: 'create-dashboard-form'}}
                    initialValues={initialValues}
                >
                    {({values, form}) => {
                        if (values.showPrices === false && values.showTransactions === true) {
                            form.change('showTransactions', false);
                        }
                        return (
                            <>
                                <DialogContent dividers sx={{paddingBlockStart: 2, paddingBlockEnd: 3}}>
                                    <Grid2 container spacing={2}>
                                        <Grid2 xs={12}>
                                            <Typography variant={'body2'} sx={{marginBlockEnd: 2}}>
                                                You can select the configurations for printing your order. Note that you can save your preferences for future use as well.
                                            </Typography>
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.Checkboxes
                                                color={'primary'}
                                                icon={<CheckboxIcon/>}
                                                checkedIcon={<ActiveCheckboxIcon/>}
                                                data={{
                                                    value: values.showAddresses,
                                                    label: 'Show Addresses',
                                                }}
                                                helperText={'If checked, the billing and shipping addresses will be shown in the printout.'}
                                                name={'showAddresses'}
                                                required={required.showAddresses}
                                                formControlLabelProps={{
                                                    sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                                }}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.Checkboxes
                                                color={'primary'}
                                                icon={<CheckboxIcon/>}
                                                checkedIcon={<ActiveCheckboxIcon/>}
                                                data={{
                                                    value: values.showPaymentTerms,
                                                    label: 'Include Payment Terms',
                                                }}
                                                helperText={'If checked, the payment terms of the order will be included in the printout.'}
                                                name={'showPaymentTerms'}
                                                required={required.showPaymentTerms}
                                                formControlLabelProps={{
                                                    sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                                }}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.Checkboxes
                                                color={'primary'}
                                                icon={<CheckboxIcon/>}
                                                checkedIcon={<ActiveCheckboxIcon/>}
                                                data={{
                                                    value: values.showPrices,
                                                    label: 'Show Prices',
                                                }}
                                                helperText={'If checked, the product pricing and the pricing summary will be shown in the printout.'}
                                                name={'showPrices'}
                                                required={required.showPrices}
                                                formControlLabelProps={{
                                                    sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                                }}
                                            />
                                        </Grid2>
                                        {
                                            values.showPrices &&
                                            <Grid2 xs={12} sx={{paddingInlineStart: 5}}>
                                                <MuiRFF.Checkboxes
                                                    color={'primary'}
                                                    icon={<CheckboxIcon/>}
                                                    checkedIcon={<ActiveCheckboxIcon/>}
                                                    data={{
                                                        value: values.showTransactions,
                                                        label: 'Show Transactions',
                                                    }}
                                                    helperText={'If checked, the applied transactions (payments, credits, etc.) will be shown in the printout.'}
                                                    name={'showTransactions'}
                                                    required={required.showTransactions}
                                                    formControlLabelProps={{
                                                        sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                                    }}
                                                />
                                            </Grid2>
                                        }

                                    </Grid2>
                                </DialogContent>
                                <DialogActions className="p-0">
                                    <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                                        <FormControlLabel
                                            label="Remember my settings"
                                            sx={{margin: 0}}
                                            control={
                                                <Checkbox
                                                    color={'primary'}
                                                    icon={<CheckboxIcon/>}
                                                    checkedIcon={<ActiveCheckboxIcon/>}
                                                    checked={hasDefaultSettings}
                                                    onChange={(e, checked) => setHasDefaultSettings(checked)}
                                                />
                                            }
                                        />
                                        <Button
                                            variant="dialog"
                                            color="primary"
                                            type="submit"
                                            form="create-dashboard-form"
                                            sx={{borderRadius: '10px 0 10px 0'}}
                                        >
                                            {hasDefaultSettings ? 'Save & Print' : 'Print'}
                                        </Button>
                                    </Box>
                                </DialogActions>
                            </>
                        );
                    }}
                </Form>
            }
        </Dialog>
    );
};

export default PrintOrderConfigurationsDialog;